import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Adddispatchheader from "./adddispatchheader";
import AddDispatchform from "./adddispatchform";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import CancelButton from "../common/ui/cancelbutton";
import Button from "../common/ui/button";
import { useAuth } from "../../context/AuthContext"; // Auth context
import WarningModal from "../common/ui/warningmodel";

export default function AddDispatch() {
  const [entries, setEntries] = useState([]);
  const [binumber, setBINumber] = useState([]);
  const [loadingBINumber, setLoadingBINumber] = useState(false);
  const [exbondNumber, setExbondNumber] = useState([]);
  const [loadingExbondNumber, setLoadingExbondNumber] = useState(false);
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const [editEntry, setEditEntry] = useState(null);
  const [editIndex, setEditIndex] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;
  const isEditMode = !!editData;

  const { logout } = useAuth();

  useEffect(() => {
    if (isEditMode) {
      const prefilledEntries = (editData.child || []).map((c) => ({
        itemId: c.id, // ✅ Corrected from exbondChildID
        beNumber: c.beNumber,
        exbondNo: c.exbondID,
        material: c.material,
        weight: c.exbondWeight,
        customer: c.customer,
        dispatchDate: c.dispatchDate,
        dispatchWeight: c.dispatchWeight,
        truckNo: c.truckNumber,
      }));
      setEntries(prefilledEntries);
    }
  }, [isEditMode, editData]);

  // Add or Update entry
  const handleAddEntry = (newEntry) => {
    if (editIndex !== null) {
      const updated = [...entries];
      updated[editIndex] = newEntry;
      setEntries(updated);
      setEditEntry(null);
      setEditIndex(null);
    } else {
      setEntries((prev) => [...prev, newEntry]);
    }
  };

  const handleDeleteEntry = (index) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditEntry = (index) => {
    setEditEntry(entries[index]);
    setEditIndex(index);
  };

  // ✅ Submit or Update dispatch
  const handleSubmit = async () => {
    if (entries.length === 0) {
      setWarningMessage(entries.message || "Please add at least one entry before submitting.");
      setWarningVisible(true);
      return;
    }

    // Total weight
    const totalWeight = entries.reduce(
      (sum, entry) => sum + Number(entry.dispatchWeight || 0),
      0
    );

    // Payload for API
    const payload = {
      total_dispatch_weight: totalWeight,
      dispatchchild: entries.map((entry) => ({
        exbond_child_id: entry.itemId, // ⚠️ Use exbond_child_id for create
        dispatch_date: entry.dispatchDate,
        dispatch_weight: Number(entry.dispatchWeight),
        truck_no: entry.truckNo,
      })),

    };

    setSubmitting(true);

    try {
      let url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.CREATE}`;
      let method = "POST";
      const callApi = async () =>
        await fetch(url, {
          method,
          headers: getHeaders(true),
          body: JSON.stringify(payload),
        });

      // Initial API call
      let response = await callApi();

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry the same API call
          response = await callApi();
        } else {
          console.error("Refresh token failed. Logging out...");
          logout();
          return;
        }
      }

      const result = await response.json();

      if (result.status === 201 || result.status === 200) {
        setEntries([]);
        navigate("/dispatch", {
          state: {
            showToast: true,
            message: result.message || (isEditMode ? "Dispatch updated successfully!" : "Dispatch created successfully!"),
          },
        });
      } else {
        setWarningMessage(result.message || "Failed to submit dispatch entry.");
        setWarningVisible(true);
      }
    } catch (error) {
      setWarningMessage("Something went wrong while submitting dispatch.");
      setWarningVisible(true);
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };


  const fetchBINumber = useCallback(async () => {
    try {
      setLoadingBINumber(true);
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_BI_NUMBER}`,
        { method: "GET", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_BI_NUMBER}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }


      const result = await response.json();
      if (result.status === 200 && Array.isArray(result.data)) {
        setBINumber(result.data.map((item) => ({
          label: item.binumber.toUpperCase(),
          value: item.id,
          be_number: item.binumber,
        })));
      } else setBINumber([]);
    } catch (error) {
      setBINumber([]);
    } finally {
      setLoadingBINumber(false);
    }
  }, [logout]);

  const fetchExbondNumber = useCallback(async () => {
    try {
      setLoadingExbondNumber(true);

      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_EXBOND_NUMBER}`,
        { method: "GET", headers: getHeaders(true) }
      );

      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISPATCH.GET_EXBOND_NUMBER}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout();
          return;
        }
      }

      const result = await response.json();

      if (result.status === 200 && Array.isArray(result.data)) {
        setExbondNumber(
          result.data.map((item) => ({
            label: `EI ${item.exbond_id}`,
            value: item.exbond_master_id,
            exbond_id: item.exbond_id,
          }))
        );
      } else setExbondNumber([]);
    } catch (error) {
      setExbondNumber([]);
    } finally {
      setLoadingExbondNumber(false);
    }
  }, [logout]);


  useEffect(() => {
    fetchBINumber();
    fetchExbondNumber();
  }, [fetchBINumber, fetchExbondNumber]);

  const canSubmit = entries.length > 0 && !submitting;

  return (
    <>
      <div className="relative mx-auto w-full px-3 sm:px-4 md:px-6">
        <Adddispatchheader />

        <AddDispatchform
          onAddEntry={handleAddEntry}
          biNumberOptions={binumber}
          loadingBINumber={loadingBINumber}
          exbondNumberOptions={exbondNumber}
          loadingExbondNumber={loadingExbondNumber}
          editEntry={editEntry}
          entries={entries}
          onDeleteEntry={handleDeleteEntry}
          onEditEntry={handleEditEntry}
        />

        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-6">
          <CancelButton
            type="button"
            className="w-full sm:w-auto"
            onClick={() => navigate("/dispatch")}
          >
            Cancel
          </CancelButton>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full sm:w-auto order-1 sm:order-2"
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </div>
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}
